import { NetworkStatus, useQuery } from '@apollo/client';
import { Alert, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import BlockLoading from 'src/shared/ui/components/block-loading/block-loading';
import { Button,  Modal } from 'src/shared/ui/elements';
import { getAvailabilityPeriods } from '../graphql/availability-query';
import { FranchiseAvailability } from './franchiseAvailabilityCheckboxes';

export const DeleteBulkAvailabilities = ({
  $title = 'Delete Bulk Availability',
  $message,
  $onConfirm,
  $loading,
  $confirmBtnText = "Remove",
  $cancelBtnText = 'Cancel',
  $maxWidth = "40rem",
  franchises = [],
  coachId,
  ...props
}) => {
  const coachFranchises = useMemo(() => {
    const unsortedFranchises = franchises.map((f) => ({
      ...f,
      disabled: !f?.settings?.canManageAvailability,
    })) || [];
    return unsortedFranchises.sort((a, b) => {
      if (a.disabled === b.disabled) {
        return a.name.localeCompare(b.name);
      }
      return a.disabled - b.disabled;
    });
  }, [franchises]);
  const [selectedAvailabilities, setSelectedAvailabilities] = useState([]);
  const {
    data:availabilityData,
    previousData: previousAvailabilityPeriodData,
    loading:availabilityLoading,
    networkStatus,
  } = useQuery(getAvailabilityPeriods, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        coachId: Number(coachId),
        showGeneralAvailability:false,
      },
    },
    onCompleted: (data) => {
      setSelectedAvailabilities(data?.availabilityPeriodsFranchise.reduce((acc, { franchise, franchiseId, periods }) => {
        const disabled = !franchise?.settings?.canManageAvailability;
        acc[franchiseId] = disabled ? [] : periods.map((p) => p.id);
        return acc;
      }, {}))
    }
  }
);
  const availabilityPeriodData = availabilityData?.availabilityPeriodsFranchise || previousAvailabilityPeriodData?.availabilityPeriodsFranchise;

 


  const handleCheckboxChange = (franchiseId, periodId) => {
    setSelectedAvailabilities((prevSelected) => {
      const updated = { ...prevSelected };
      if (updated[franchiseId]?.includes(periodId)) {
        updated[franchiseId] = updated[franchiseId].filter((id) => id !== periodId);
      } else {
        updated[franchiseId] = [...(updated[franchiseId] || []), periodId];
      }
      return updated;
    });
  };

  const handleSelectAll = (franchiseId) => {
    setSelectedAvailabilities((prevSelected) => {
      const franchisePeriods = availabilityPeriodData?.find((a) => +a.franchiseId === +franchiseId)?.periods || [];
      const allSelected = prevSelected[franchiseId]?.length === franchisePeriods.length;
      return {
        ...prevSelected,
        [franchiseId]: allSelected ? [] : franchisePeriods.map((p) => p.id),
      };
    });
  };

  const handleConfirm = async () => {
    const payload = Object.entries(selectedAvailabilities)
  .filter(([franchiseId, periods]) => franchiseId !== "null" && periods.length > 0) // Skip null franchiseId or empty periods
  .map(([franchiseId, periods]) => ({
    franchiseId,
    periods
  }));
    await $onConfirm(payload);
    props.$actions.close();
  };


  const showCheckboxes = useMemo(() => {
    return franchises.some(
      (f) => f?.settings?.canManageAvailability
    );
  }, [franchises]);

  return (
    <Modal $title={$title} style={{ maxWidth: $maxWidth }} {...props}>
      <BlockLoading loading={availabilityLoading || networkStatus === NetworkStatus.loading}>
      <Modal.Content>
         <Alert severity="error">
            <span>{$message}</span>
          </Alert>
        {showCheckboxes && coachFranchises?.length > 0 ?
              (<div>
              {coachFranchises.map((franchise) => {
                  const franchisePeriods = availabilityPeriodData?.find(
                    (a) => +a.franchiseId === +franchise.id
                  )?.periods || [];
                  return (
                    <FranchiseAvailability
                      open={coachFranchises.length === 1}
                      disabled={franchise.disabled}
                key={franchise.id}
                franchise={franchise}
                franchisePeriods={franchisePeriods}
                selectedPeriods={selectedAvailabilities[franchise.id] || []}
                onToggleAll={() => handleSelectAll(franchise.id)}
                onTogglePeriod={(periodId) => handleCheckboxChange(franchise.id, periodId)}
              />
                  );
                })}
              </div>) : 
         ( <Alert severity="info">
            <Typography variant="body1">
              Please contact your manager to update your availability.
            </Typography>
          </Alert>)}
      </Modal.Content>
      <Modal.Footer className="flex align-center justify-flex-end">
        {showCheckboxes && (
          <Button
            type="button"
            className="tw-bg-red tw-border-red"
            $loading={$loading}
            onClick={handleConfirm}
            disabled={Object.values(selectedAvailabilities).every((sel) => sel.length === 0)}
          >
            {$confirmBtnText}
          </Button>
        )}
        <Button
          className="ml-12 outline tw-text-red tw-border-red"
          onClick={props.$actions.close}
          type="button"
        >
          {$cancelBtnText}
        </Button>
        </Modal.Footer>
        </BlockLoading>
    </Modal>
  );
};

